// types/devices.ts
// Request/Response interfaces
export interface RegisterDeviceRequest {
    deviceName: string;
    locationId?: number;
    ipAddress?: string;
    serialNumber: string;
}

export interface RegisterDeviceResponse {
    deviceId: number;
    deviceToken: string;
}

export interface UpdateDeviceRequest {
    deviceName?: string;
    serialNumber?: string;  // ← Added this property
    locationId?: number;
    active?: boolean;
}

// System status and metrics interfaces
export interface SystemStatusResponse {
    deviceId: number;
    deviceName: string;
    location: string;
    status: string;
    onlineStatus: string;
    currentPlaylist?: string;
    lastSync: string;
    metrics: SystemStatusMetrics;
    statusReasons?: string[];
}

export interface SystemStatusMetrics {
    cpuUsage: number;
    memoryUsage: number;
    diskUsage: number;
    temperature?: number;
}

// Define a string literal type for device status
export type DeviceStatus = 'Online' | 'Offline' | 'Warning' | 'Maintenance' | 'Pending';

export type DeviceOnlineStatus = 'Online' | 'Offline' | 'Offline+15' | 'Offline+30' | 'Pending';

// Base device interface
export interface Device {
    deviceId: number;
    deviceName: string;
    serialNumber: string;
    locationId?: number;
    ipAddress: string;
    lastHeartbeat?: Date;
    lastSyncTime?: Date;
    status: DeviceStatus;
    onlineStatus: DeviceOnlineStatus;  // ADD THIS LINE
    cacheSize?: number;
    active?: boolean;
    currentStatus?: SystemStatusMetrics;
    location?: DeviceLocationInfo;
    deviceToken?: string;  // Optional since it might not always be included for security
    piConnectUrl?: string; // Add this - URL for Pi Connect registration
    statusReasons?: string[]; // Short reasons behind a non-healthy status (from SystemStatus)
}

    // ADD THIS NEW INTERFACE
export interface DeviceLocationInfo {
    locationName: string;
    city: string;
    address: string;
}

export interface AddDeviceRequest {
    deviceName: string;
    locationId?: number;
    username?: string;
    password?: string;
    enableSsh?: boolean;
    timezone?: string;
    keyboardLayout?: string;
    wifiSsid?: string;
    wifiPassword?: string;
    wifiCountry?: string;
}

export interface AddDeviceResponse {
    deviceId: number;
    deviceToken: string;
    deviceName: string;
    companyId: number;
    jwtToken: string;  // ADD THIS LINE
}

// Helper function to check if a device is considered online
export const isDeviceOnline = (onlineStatus: DeviceOnlineStatus): boolean => {
    return onlineStatus === 'Online';
};

// Helper function to get a user-friendly display for online status
export const getOnlineStatusDisplay = (onlineStatus: DeviceOnlineStatus): string => {
    switch (onlineStatus) {
        case 'Online':
            return 'Online';
        case 'Pending':
            return 'Pending';
        case 'Offline':
            return 'Offline (5-15 min)';
        case 'Offline+15':
            return 'Offline (15-30 min)';
        case 'Offline+30':
            return 'Offline (30+ min)';
        default:
            return onlineStatus;
    }
};

// Helper function to get color class for online status
export const getOnlineStatusColor = (onlineStatus: DeviceOnlineStatus): string => {
    switch (onlineStatus) {
        case 'Online':
            return 'text-green-600';
        case 'Offline':
            return 'text-orange-600';
        case 'Offline+15':
            return 'text-red-500';
        case 'Offline+30':
            return 'text-red-700';
        case 'Pending':
            return 'text-gray-500';
        default:
            return 'text-gray-600';
    }
};